'use client';

import type { FC, MouseEvent } from 'react';
import { useCallback } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import { useLocalizedHref } from '../../hooks/useLocalizedHref';
import tabTranslations from '../TabBar.translations';
import gameTranslations from '../../screens/GamesScreen/GamesScreen.translations';
import { createTranslations } from '../../i18n/types';

const navTranslations = createTranslations({
  en: { bonuses: 'Bonuses' },
  ru: { bonuses: 'Бонусы' },
  uz: { bonuses: 'Bonuslar' },
  fa: { bonuses: 'بونوس‌ها' },
  pt: { bonuses: 'Bônus' },
  tr: { bonuses: 'Bonuslar' },
  uk: { bonuses: 'Бонуси' },
  de: { bonuses: 'Boni' },
  es: { bonuses: 'Bonos' },
  pl: { bonuses: 'Bonusy' },
});

import { useRushMidConfig } from '../../queries/useRushMidConfig';
import { useProfileData } from '../../queries/useProfileQueries';
import { useIsAuthed } from '../../hooks/useIsAuthed';
import { useIntentPrefetch } from '../../hooks/useIntentPrefetch';
import { isWikiPath } from '../../lib/wikiPath';
import { useNavigation } from '../../contexts/NavigationContext';
import { useScrollManager } from '../../contexts/ScrollManagerContext';
import { TAB_URL_MAP, buildScreenUrl } from '../../lib/screen-url-map';
import {
  FaGift, GiOpenChest, FaClipboardList, FaUsers,
  GiCrossedSwords, FaAtom, FaArrowUp, FaStore, FaCrosshairs, FaSkull,
} from '../icons';
import type { IconType } from '../icons';
import type { TabId } from '../../types/tabs';
import type { ScreenName } from '../../types/navigation';
import { useHighlightedTab } from '../../hooks/useHighlightedTab';
import { cn } from '../../utils/cn';

interface NavItemBase {
  id: string;
  tabId: TabId;
  icon: IconType;
  neon?: string;
}

type NavItem =
  | (NavItemBase & { type: 'tab'; translationSource: 'nav'; labelKey: keyof typeof navTranslations.en })
  | (NavItemBase & { type: 'tab'; translationSource: 'tab'; labelKey: keyof typeof tabTranslations.en })
  | (NavItemBase & {
      type: 'game';
      translationSource: 'game';
      labelKey: keyof typeof gameTranslations.en;
      screen: ScreenName;
    });

const navItems: NavItem[] = [
  { type: 'tab', id: 'balance', tabId: 'balance', icon: FaGift, labelKey: 'bonuses', translationSource: 'nav', neon: '#f472b6' },
  { type: 'tab', id: 'cases', tabId: 'cases', icon: GiOpenChest, labelKey: 'cases', translationSource: 'tab', neon: '#f59e0b' },
  { type: 'game', id: 'rush-mid', tabId: 'games', screen: 'rush-mid', icon: FaCrosshairs, labelKey: 'rushMidTitle', translationSource: 'game', neon: '#f43f5e' },
  { type: 'game', id: 'roulette', tabId: 'games', screen: 'roulette', icon: FaSkull, labelKey: 'rouletteTitle', translationSource: 'game', neon: '#dc2626' },
  { type: 'game', id: 'case-battles', tabId: 'games', screen: 'case-battles', icon: GiCrossedSwords, labelKey: 'caseBattlesTitle', translationSource: 'game', neon: '#ef4444' },
  { type: 'game', id: 'contracts', tabId: 'games', screen: 'contracts', icon: FaAtom, labelKey: 'contractsTitle', translationSource: 'game', neon: '#a78bfa' },
  { type: 'game', id: 'upgrade', tabId: 'games', screen: 'upgrade', icon: FaArrowUp, labelKey: 'upgradeTitle', translationSource: 'game', neon: '#34d399' },
  { type: 'game', id: 'shop', tabId: 'games', screen: 'shop', icon: FaStore, labelKey: 'shopTitle', translationSource: 'game', neon: '#60a5fa' },
  { type: 'tab', id: 'inventory', tabId: 'inventory', icon: FaClipboardList, labelKey: 'inventory', translationSource: 'tab', neon: '#38bdf8' },
  { type: 'tab', id: 'referrals', tabId: 'referrals', icon: FaUsers, labelKey: 'referrals', translationSource: 'tab', neon: '#4ade80' },
];

export const DesktopNav: FC = () => {
  const tTab = useLocalTranslation(tabTranslations);
  const tGame = useLocalTranslation(gameTranslations);
  const tNav = useLocalTranslation(navTranslations);
  const navigation = useNavigation();
  const localize = useLocalizedHref();
  const { clearAll: clearAllScroll } = useScrollManager();
  const onWiki = isWikiPath(usePathname());
  const isAuthed = useIsAuthed();
  // WHY: конфиг Rush Mid отдаётся только вошедшему — гость на вики получал три 401 подряд (запрос + два ретрая)
  const rushMid = useRushMidConfig({ enabled: !onWiki || isAuthed });
  const intentPrefetch = useIntentPrefetch();
  const profile = useProfileData();
  const rouletteLocked = (profile?.rouletteRestrictedMode ?? true) && !(profile?.skinsExchangeEnabled ?? false);
  const items = navItems.filter(
    (it) => (it.id !== 'rush-mid' || !rushMid.comingSoon) && (it.id !== 'roulette' || !rouletteLocked),
  );

  const highlightedTab = useHighlightedTab();
  const activeTab = navigation.state.activeTab;
  const currentStack = navigation.state.stacks[activeTab];
  const stackScreens = currentStack.map((s) => s.screen);

  const handleClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>, item: NavItem) => {
      if (
        event.metaKey || event.ctrlKey || event.shiftKey ||
        event.altKey || event.button !== 0
      ) {
        return;
      }
      event.preventDefault();
      clearAllScroll();
      if (item.type === 'tab') {
        navigation.changeTab(item.tabId);
        navigation.resetStack(item.tabId, item.tabId);
      } else {
        navigation.changeTab('games');
        navigation.resetStack('games', 'games');
        navigation.pushScreen('games', item.screen);
      }
    },
    [navigation, clearAllScroll],
  );

  const itemHref = (item: NavItem): string =>
    localize(item.type === 'tab' ? TAB_URL_MAP[item.tabId] : buildScreenUrl(item.screen));

  const isActive = (item: NavItem) => {
    if (item.type === 'tab') {
      return highlightedTab === item.tabId;
    }
    if (highlightedTab === 'games' && stackScreens.includes(item.screen)) return true;
    return highlightedTab === item.screen;
  };

  return (
    <nav className="relative border-b border-white/[0.04] px-6 h-12 flex items-center justify-center gap-0.5 shrink-0 overflow-x-auto [scrollbar-width:none]">
      {items.map((item) => {
        const active = isActive(item);
        const label =
          item.translationSource === 'nav'
            ? tNav.t(item.labelKey)
            : item.translationSource === 'tab'
              ? tTab.t(item.labelKey)
              : tGame.t(item.labelKey);

        const neon = item.neon || '#fff';

        return (
          <Link
            key={item.id}
            href={itemHref(item)}
            {...(onWiki ? intentPrefetch(itemHref(item)) : { prefetch: true })}
            className={cn(
              'relative flex items-center gap-1.5 px-3.5 py-1.5 border-none cursor-pointer text-xs uppercase tracking-wider whitespace-nowrap bg-transparent outline-none transition-all duration-200 font-medium no-underline',
              active
                ? 'text-white'
                : 'text-white/35 hover:text-white/60',
            )}
            style={active ? {
              textShadow: `0 0 8px ${neon}80, 0 0 16px ${neon}40`,
            } : undefined}
            onClick={(e) => handleClick(e, item)}
          >
            <span>{label}</span>


            {active && (
              <span
                className="absolute bottom-0 start-2 end-2 h-[2px] rounded-full"
                style={{
                  background: neon,
                  boxShadow: `0 0 6px ${neon}, 0 0 12px ${neon}80, 0 2px 8px ${neon}60`,
                }}
              />
            )}
          </Link>
        );
      })}
    </nav>
  );
};
